import { toast } from "react-toastify";
import { call, put, takeLatest } from "redux-saga/effects";
import * as actions from "./Actions";
import * as api from "./Api";
import Types from "./Types";

const extractList = (body, keys = ["data"]) => {
  if (Array.isArray(body)) return body;
  for (const key of keys) {
    if (Array.isArray(body?.[key])) return body[key];
  }
  if (body?.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    for (const key of keys) {
      if (Array.isArray(body.data[key])) return body.data[key];
    }
  }
  if (Array.isArray(body?.data?.data)) return body.data.data;
  return [];
};

function* getPracticeUsers(formData) {
  try {
    yield put(actions.practiceUsersLoading(true));
    const result = yield call(api.getPracticeUsers, formData);
    const body = result.data;
    const data = extractList(body, ["data", "users", "items"]);
    const pagination = body?.pagination || body?.data?.pagination || body?.meta || {};
    const count =
      pagination.total_count ||
      pagination.total ||
      body?.total ||
      data.length;
    const lastPage =
      pagination.last_page ||
      pagination.total_pages ||
      Math.ceil(count / (pagination.per_page || 10)) ||
      1;
    yield put(actions.getPracticeUsersSuccess({ data, count, lastPage }));
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to fetch practice users"
    );
    yield put(actions.getPracticeUsersFail());
  } finally {
    yield put(actions.practiceUsersLoading(false));
  }
}

function* getPracticeSubjects() {
  try {
    yield put(actions.practiceSubjectsLoading(true));
    const result = yield call(api.getPracticeSubjects);
    const data = extractList(result.data, ["data", "subjects"]);
    yield put(actions.getPracticeSubjectsSuccess(data));
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to fetch practice subjects"
    );
    yield put(actions.getPracticeSubjectsFail());
  } finally {
    yield put(actions.practiceSubjectsLoading(false));
  }
}

function* getActiveSubscriptions({ payload }) {
  try {
    yield put(actions.activeSubscriptionsLoading(true));
    const result = yield call(api.getActiveSubscriptions, payload);
    const data = extractList(result.data, ["data", "subscriptions"]);
    yield put(actions.getActiveSubscriptionsSuccess(data));
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to fetch active subscriptions"
    );
    yield put(actions.getActiveSubscriptionsFail());
  } finally {
    yield put(actions.activeSubscriptionsLoading(false));
  }
}

function* createPracticeSubscription({ payload, action }) {
  try {
    yield put(actions.createSubscriptionLoading(true));
    const result = yield call(api.createPracticeSubscription, payload);
    yield put(actions.createPracticeSubscriptionSuccess(result.data));
    toast.success(result.data?.message || "Subscription created successfully");
    if (action) {
      action(result.data);
    }
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to create subscription"
    );
    yield put(actions.createPracticeSubscriptionFail());
  } finally {
    yield put(actions.createSubscriptionLoading(false));
  }
}

function* sendBulkCustomMessageToPhones({ payload, action }) {
  try {
    yield put(actions.sendBulkMessageLoading(true));
    const result = yield call(api.sendBulkCustomMessageToPhones, payload);
    yield put(actions.sendBulkCustomMessageToPhonesSuccess(result.data));
    toast.success(
      result.data?.message || "Bulk custom message sent successfully"
    );
    if (action) {
      action(result.data);
    }
  } catch (error) {
    toast.error(
      error?.response?.data?.message || "Failed to send bulk custom message"
    );
    yield put(actions.sendBulkCustomMessageToPhonesFail());
  } finally {
    yield put(actions.sendBulkMessageLoading(false));
  }
}

export default function* AsqueraPracticeSaga() {
  yield takeLatest(Types.GET_PRACTICE_USERS_REQUEST, getPracticeUsers);
  yield takeLatest(Types.GET_PRACTICE_SUBJECTS_REQUEST, getPracticeSubjects);
  yield takeLatest(
    Types.GET_ACTIVE_SUBSCRIPTIONS_REQUEST,
    getActiveSubscriptions
  );
  yield takeLatest(
    Types.CREATE_PRACTICE_SUBSCRIPTION_REQUEST,
    createPracticeSubscription
  );
  yield takeLatest(
    Types.SEND_BULK_CUSTOM_MESSAGE_TO_PHONES_REQUEST,
    sendBulkCustomMessageToPhones
  );
}
